/* eslint-disable @typescript-eslint/no-explicit-any */
import { observable, configure, flow, action } from 'mobx'
import API from '@aws-amplify/api'
import _ from 'lodash'
import Fuse from 'fuse.js'
import * as queries from '../graphql/queries'

import { ToastType } from '../components/Dashboard/Toasts'

configure({ enforceActions: 'always' })

const commonUrlExtensions = [
  '.com',
  '.org',
  '.net',
  '.edu',
  '.gov',
  '.mil',
  '.int',
  '.io',
  '.co',
  '.uk',
  '.ca',
  '.au',
  '.de',
  '.fr',
  '.jp',
  '.cn',
  '.ru',
]

// eslint-disable-next-line @typescript-eslint/class-name-casing
export interface filterProps {
  isHot: boolean
  status: string | null
  addedBy: string | null
  maneuver: string | null
  missionName: string | null
  noSelectedFilter: string | null
  rank: string | null
}
interface SearchProps {
  toasts: ToastType[]
  isLoading: boolean
  cacheValid: boolean
  prospects: Prospect[]
  filteredProspects: Prospect[]
  sortOrder: 'asc' | 'desc'
  searchQuery: string
  selectedFilters: filterProps
  nextToken: string | null
}

export const initialState: SearchProps = {
  toasts: [],
  isLoading: false,
  cacheValid: false,
  prospects: [],
  filteredProspects: [],
  sortOrder: 'asc',
  searchQuery: '',
  selectedFilters: {
    isHot: false,
    status: 'All',
    addedBy: 'All',
    maneuver: 'All',
    missionName: 'All',
    noSelectedFilter: 'null',
    rank: 'All',
  },
  nextToken: 'start',
}

export interface Prospect {
  leadUpdateDate: string
  rank: unknown
  isHot: null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  missionName: any
  email: string
  id: number
  name: string
  phoneNumber: string
  firstName: string
  lastName: string
  company: string
  addedBy: string
  status: string
  socials: []
  industry: string
  title: string
  maneuver: string
  sendDates: RecentTouch[]
}

interface RecentTouch {
  touch: string
  sendDate: string
  uuid: string
}

const fuseOptions = {
  isCaseSensitive: false,
  findAllMatches: false,
  includeScore: false,
  useExtendedSearch: false,
  includeMatches: true,
  minMatchCharLength: 3,
  shouldSort: true,
  threshold: 0.2,
  distance: 200,
  keys: [
    'email',
    'name',
    'company',
    'industry',
    'status',
    'title',
    'maneuver',
    'rank',
    'missionName',
    'leadTitle',
  ],
}

export class Search {
  @observable toasts: ToastType[]
  @observable isLoading: boolean
  @observable cacheValid: boolean
  @observable prospects: Prospect[]
  @observable filteredProspects: Prospect[]
  @observable sortOrder: 'asc' | 'desc'
  @observable searchQuery: string
  @observable nextToken: string | null = 'start'
  @observable selectedFilter: filterProps
  @observable isDataFiltered: boolean
  @observable EngagementHighlightFilter: any

  constructor() {
    this.init()
  }

  @action
  init(): void {
    this.setToasts(initialState.toasts)
    this.setIsLoading(initialState.isLoading)
    this.setCacheValid(initialState.cacheValid)
    this.setProspects(initialState.prospects)
    this.setFilteredProspects(initialState.filteredProspects)
    this.setSortOrder(initialState.sortOrder)
    this.setSearchQuery(initialState.searchQuery)
    this.setSelectedFilter(initialState.selectedFilters)
    this.setEngagementHighlightFilter({})
    this.setNextToken(initialState.nextToken)
  }

  @action
  pushToToasts(toast: ToastType): void {
    const newToast = toast

    if (newToast.show === undefined) {
      newToast.show = true
    }

    if (newToast.delay === undefined) {
      newToast.delay = 3000
    }

    this.setToasts(this.toasts.concat(newToast))
  }

  @action
  popFromToasts(): void {
    const newToasts = [...this.toasts]

    newToasts.shift()
    this.setToasts(newToasts)
  }

  @action
  setToasts(toasts: ToastType[]): void {
    this.toasts = toasts
  }

  @action
  setIsLoading(isLoading: boolean): void {
    this.isLoading = isLoading
  }

  @action
  setCacheValid(cacheValid: boolean): void {
    this.cacheValid = cacheValid
  }

  @action
  setProspects(prospects: Prospect[]): void {
    this.prospects = prospects
    this.filterProspects()
  }

  @action
  setNextToken(nextToken): void {
    this.nextToken = nextToken
  }

  @action
  setFilteredProspects(filteredProspects: Prospect[]): void {
    this.filteredProspects = filteredProspects
  }

  @action
  setSortOrder(sortOrder: 'asc' | 'desc'): void {
    this.sortOrder = sortOrder

    this.setFilteredProspects(this.sort(this.filteredProspects))
    this.setProspects(this.sort(this.prospects))
  }

  @action
  setSearchQuery(searchQuery: string): void {
    this.searchQuery = searchQuery
    this.filterProspects()
  }

  @action
  setEngagementHighlightFilter(FilterObj): void {
    this.EngagementHighlightFilter = FilterObj
    this.filterProspects()
  }

  @action
  setSelectedFilter(filter: filterProps): void {
    this.selectedFilter = filter
    if (JSON.stringify(filter) === JSON.stringify(initialState.selectedFilters)) {
      this.setisDataFiltered(false)
    } else {
      this.setisDataFiltered(true)
    }

    this.filterProspects()
  }

  @action
  setisDataFiltered(value: boolean): void {
    this.isDataFiltered = value
  }

  @action
  resetFilters(): void {
    this.setSelectedFilter(initialState.selectedFilters)
  }

  @action
  onChangeQuery(searchQuery: string): void {
    this.setSearchQuery(searchQuery)
  }

  @action
  filterProspects(): void {
    let data = [...this.prospects]

    if (this.EngagementHighlightFilter?.filterType?.length > 0) {
      data = data.filter(item => this.EngagementHighlightFilter?.leadsEmails[item?.email])
    }

    if (this.searchQuery) {
      let value = this.searchQuery
      const extention = commonUrlExtensions.find(item => value.includes(item))
      if (extention) {
        const extentionIndex = value.indexOf(extention)
        if (extentionIndex !== -1) {
          value = value.slice(0, extentionIndex)
        }
      }

      // Create the Fuse index
      const myIndex = Fuse.createIndex(
        [
          'email',
          'name',
          'company',
          'industry',
          'status',
          'title',
          'maneuver',
          'rank',
          'missionName',
        ],
        this.prospects,
      )
      // initialize Fuse with the index
      const fuse = new Fuse(data, fuseOptions, myIndex)
      data = fuse.search(value).map(r => r.item)
    }

    if (this.isDataFiltered) {
      const filter = this.selectedFilter

      if (filter?.isHot) {
        data = data.filter(item => item.isHot !== null && item.isHot)
      }
      if (filter?.status) {
        const filterStatus = filter.status ?? 'All'

        if (
          filterStatus !== 'All' &&
          filterStatus !== 'no-action' &&
          filterStatus !== 'do-not-contact'
        ) {
          data = data.filter(
            item => item.status && item.status.toLowerCase().includes(filterStatus.toLowerCase()),
          )
        }

        if (filterStatus === 'do-not-contact') {
          data = data.filter(
            item => item.status !== null && item.status.toLowerCase().includes('do not contact'),
          )
        }
      }

      if (filter?.status === 'no-action' && filter?.noSelectedFilter) {
        const noActionType = filter?.noSelectedFilter

        if (noActionType === 'Mission Complete') {
          data = data.filter(
            item => item.status !== null && item.status.toLowerCase().includes('mavericks ready'),
          )
        } else if (noActionType === '6+ months') {
          data = data.filter(
            item => item.status !== null && item.status.toLowerCase().includes("6+ month's"),
          )
        } else {
          data = data.filter(
            item =>
              item.status !== null &&
              item.status.toLowerCase().includes(noActionType.toLowerCase()),
          )
        }
      }

      if (filter?.maneuver && filter.maneuver !== 'All') {
        if (filter.maneuver === 'rev-up') {
          data = data.filter(
            item =>
              item.sendDates && item.sendDates.length > 0 && item.sendDates[0][0].includes('ft'),
          )
        } else if (filter.maneuver === 'engaged') {
          data = data.filter(
            item =>
              item.sendDates && item.sendDates.length > 0 && item.sendDates[0][0].includes('fu'),
          )
        } else if (filter.maneuver === 're-engage') {
          data = data.filter(
            item =>
              item.sendDates && item.sendDates.length > 0 && item.sendDates[0][0].includes('n'),
          )
        }
      }

      if (filter?.missionName && filter?.missionName !== 'All') {
        data = data.filter(item => item.missionName === filter.missionName)
      }

      if (filter?.rank && filter?.rank !== 'All') {
        data = data.filter(item => item.rank === filter?.rank)
      }

      if (filter?.addedBy && filter?.addedBy !== 'All') {
        if (filter.addedBy === 'maverick') {
          data = data.filter(
            item => item.addedBy !== null && item.addedBy.toLowerCase().includes('maverick'),
          )
        }

        if (filter.addedBy === 'user') {
          data = data.filter(
            item => item.addedBy !== null && item.addedBy.toLowerCase().includes('user'),
          )
        }
      }
    }

    this.setFilteredProspects(data)
  }

  sort(prospects: Prospect[]): Prospect[] {
    return _.orderBy(prospects, ['lastName'], [this.sortOrder])
  }

  @action
  getProspects = flow(function*(this: Search) {
    this.setIsLoading(true)

    try {
      const response = yield API.get('mav-outlook-api', '/leads/search', {})
      const prospects = this.sort(response)
      this.setProspects(prospects)
      this.onChangeQuery(this.searchQuery)
      this.setCacheValid(true)
      this.setIsLoading(false)
    } catch (e) {
      this.setProspects([])
      this.setCacheValid(false)
      this.setIsLoading(false)
    }
  })

  @action
  getProspectsFromGraph = flow(function*(this: Search, client) {
    let nextToken
    let prospects
    if (this.nextToken === null) {
      return
    }
    try {
      yield client
        .query({
          fetchPolicy: 'network-only',
          query: queries.queryByOwnerEmail,
          variables: { first: 10000, after: this.nextToken !== 'start' ? this.nextToken : null },
        })
        .then(({ data, errors }) => {
          if (errors) {
            console.log(errors)
          }
          prospects = data.queryByOwnerEmail.items
          nextToken = data.queryByOwnerEmail.nextToken
        })
        .catch(err => console.log(err))
        .finally(() => {
          this.setProspects(this.sort(this.prospects.concat(prospects)))
          this.setNextToken(nextToken)
          this.setIsLoading(false)
          this.onChangeQuery(this.searchQuery)
          if (this.nextToken === null) {
            this.setCacheValid(true)
          }
        })
    } catch (e) {
      this.setProspects([])
      this.setCacheValid(false)
      this.setIsLoading(false)
    }
  })

  @action
  addSingleLeadToTable(param: any): void {
    const lead = param
    lead.leadTitle = lead?.title
    lead.email = lead?.emailAddress
    lead.industry = String(lead?.industry)
    lead.mission = lead?.missionName
    lead.status = lead?.maverickStatus?.length > 0 ? lead?.maverickStatus : 'new'
    lead.dateAdded = new Date().toISOString()
    lead.sendDates = []
    lead.addedBy = 'User'
    lead.rank = null
    lead.maverickActivated = Boolean(lead.missionName?.length)

    this.setProspects(this.sort(this.prospects.concat([lead])))
  }

  @action
  getProspectsByIds = flow(function*(this: Search, client, ids) {
    let fetchedProspects

    try {
      yield client
        .query({
          fetchPolicy: 'network-only',
          query: queries.getLeadsByIds,
          variables: { ids },
        })
        .then(({ data, errors }) => {
          if (errors) {
            console.log(errors)
          }
          fetchedProspects = data.getLeadsByIds
        })
        .catch(err => console.log(err))
        .finally(() => {
          this.setProspects(this.prospects.filter(prospect => !ids.includes(prospect.id)))
          this.setProspects(this.sort(this.prospects.concat(fetchedProspects)))
          this.setIsLoading(false)
          this.onChangeQuery(this.searchQuery)
          if (this.nextToken === null) {
            this.setCacheValid(true)
          }
        })
    } catch (e) {
      this.setProspects([])
      this.setCacheValid(false)
      this.setIsLoading(false)
    }
  })

  @action
  updateProspectsData(dataToUpdate: {}): void {
    let data = [...this.prospects]
    data = data.map(item => {
      let value = item
      if (dataToUpdate[item?.id]) {
        value = { ...item, ...dataToUpdate[item?.id] }
      }
      return value
    })

    this.setProspects(data)
  }

  @action
  deleteLeads(dataToDelete: {}): void {
    let data = [...this.prospects]
    data = data.filter(item => {
      return !dataToDelete[item?.id]
    })

    this.setProspects(data)
  }
  @action
  clearState(): void {
    this.init()
  }
}

export default new Search()
